import {
    all,
    call,
    cancelled,
    fork,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';

import {
    GET_ORG_PROPERITES_COLLECTION,
    GET_PROPERTY_BY_ID,
    LOGOUT_USER,
    SET_ACTIVE_USER_ORG,
    SET_ACTIVE_USER_PROPERTY
} from '../actions/types';

import { eventChannel } from 'redux-saga';

import {
    getOrgPropertiesCollectionFailure,
    getOrgPropertiesCollectionSuccess,
    getPropertyByIdCollectionFailure,
    getPropertyByIdCollectionSuccess,
    setActiveUserPropertyFailure,
    setActiveUserPropertySuccess,
    setOrganizationProperty
} from '../actions/Properties';

import { db, func } from 'config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    updateDoc,
} from 'firebase/firestore';
import { openSnackbar } from 'store/actions/Snackbar';

import { log } from 'utils/Loggers';
import * as selectors from './Selectors';

const orgPropertiesCollectionRef = collection(db, 'org_properties');

export function* propertyCollectionWatch({ propertyId }) {
    let unsubscribePropertyCollectionData;

    const propertyDocRef = doc(db, 'properties', propertyId);

    const propertyCollectionChannel = eventChannel(emit => {
        unsubscribePropertyCollectionData = onSnapshot(propertyDocRef, propertyDoc => {
            if (propertyDoc.exists()) {
                emit(propertyDoc.data());
            } else {
                // TODO: Replace the logic once the GET APIs are added
                const currentPath = window.location.pathname;

                if (currentPath !== '/administration') {
                    emit(new Error('Property not found'));
                }
            }
        });

        return () => unsubscribePropertyCollectionData();
    });

    try {
        while (true) {
            const { userSignOut, propertyCollectionData, error } = yield race({
                userSignOut: take(LOGOUT_USER),
                propertyCollectionData: take(propertyCollectionChannel),
                error: take(propertyCollectionChannel)
            });

            if (userSignOut) {
                propertyCollectionChannel.close();
            } else if (error) {
                yield put(
                    openSnackbar({
                        open: true,
                        message: `Error: ${error.message}`,
                        variant: 'alert',
                        alert: {
                            color: 'error',
                            variant: 'outlined'
                        },
                        close: false
                    })
                );
                yield put(getPropertyByIdCollectionFailure(error));
                yield put(setActiveUserPropertyFailure(error));
            } else {
                yield put(getPropertyByIdCollectionSuccess(propertyCollectionData));
                yield put(setActiveUserPropertySuccess(propertyCollectionData));
            }
        }
    } catch (error) {
        yield put(
            openSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                variant: 'alert',
                alert: {
                    color: 'error',
                    variant: 'outlined'
                },
                close: false
            })
        );
        yield put(getPropertyByIdCollectionFailure(error));
    } finally {
        unsubscribePropertyCollectionData();
        if (yield cancelled()) {
            propertyCollectionChannel.close();
            unsubscribePropertyCollectionData();
        }
    }
}

export function* orgPropertiesCollectionWatch({ payload }) {
    const orgId = payload.org_id;
    const property = yield call(getPropertyById, payload.property_id);
    yield put(setOrganizationProperty(property));
    let unsubscribeOrgPropertiesData;

    const orgPropertiesCollectionChannel = eventChannel(emit => {
        unsubscribeOrgPropertiesData = onSnapshot(
            doc(orgPropertiesCollectionRef, orgId),
            docSnap => {
                if (docSnap.exists()) {
                    emit(docSnap.data());
                } else {
                    emit(
                        getOrgPropertiesCollectionFailure(new Error('No such document'))
                    );
                }
            }
        );

        return unsubscribeOrgPropertiesData;
    });

    try {
        while (true) {
            const { userSignOut, resettingOrg, orgPropertiesData } = yield race({
                userSignOut: take(LOGOUT_USER),
                resettingOrg: take(SET_ACTIVE_USER_ORG),
                orgPropertiesData: take(orgPropertiesCollectionChannel)
            });

            if (userSignOut || resettingOrg) {
                orgPropertiesCollectionChannel.close();
            } else {
                yield put(getOrgPropertiesCollectionSuccess(orgPropertiesData));
            }
        }
    } catch (error) {
        log('Org Properties Error: error getting org_properties doc (FS)', {
            error,
            orgId
        });
    } finally {
        unsubscribeOrgPropertiesData();
        if (yield cancelled()) {
            orgPropertiesCollectionChannel.close();
            unsubscribeOrgPropertiesData();
        }
    }
}

export function* getPropertyById(propertyId) {
    try {
        const propertyDocRef = doc(db, 'properties', propertyId);
        const propertyDoc = yield call(getDoc, propertyDocRef);

        if (propertyDoc.exists()) {
            return propertyDoc.data();
        }
    } catch (err) {
        log('Org Error: getting property by id (FS)', {
            error: err,
            propertyId
        });
    }
}

export function* getPropertyByIdCollection({ propertyId }) {
    try {
        const propertyDocRef = doc(db, 'properties', propertyId);
        const propertyDoc = yield call(getDoc, propertyDocRef);

        if (propertyDoc.exists()) {
            yield put(getPropertyByIdCollectionSuccess(propertyDoc.data()));
        } else {
            throw new Error('Property not found');
        }
    } catch (err) {
        yield put(
            openSnackbar({
                open: true,
                message: `Error: ${err}`,
                variant: 'alert',
                alert: {
                    color: 'error',
                    variant: 'outlined'
                },
                close: false
            })
        );
        yield put(getPropertyByIdCollectionFailure(err));
    }
}

export const settingActivePropertyRequest = async ({ active_property_id, userId }) => {
    try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, { active_property_id });
        return { res: true };
    } catch (error) {
        return { res: false, error };
    }
};

export function* settingActiveProperty({ payload }) {
    const { id } = payload;
    const userData = yield select(selectors._userData);
    const active_property_id = id;
    const userId = userData.uid;

    const { res, error } = yield call(() =>
        settingActivePropertyRequest({ active_property_id, userId })
    );

    if (res) {
        yield put(setActiveUserPropertySuccess(payload));

        yield put(
            openSnackbar({
                open: true,
                message: `Property set successfully.`,
                variant: 'success',
                autoHideDuration: 3000
            })
        );
    } else {
        yield put(setActiveUserPropertyFailure(error));

        yield put(
            openSnackbar({
                open: true,
                message: `Failed: Property was not set successfully.`,
                variant: 'error',
                autoHideDuration: 3000
            })
        );
    }
}

///////////// Action Creators For Root Saga //////////////////

export function* getOrgProperties() {
    yield takeLatest(GET_ORG_PROPERITES_COLLECTION, orgPropertiesCollectionWatch);
}

export function* getProperty() {
    yield takeLatest(GET_PROPERTY_BY_ID, propertyCollectionWatch);
}

export function* watchSettingActiveProperty() {
    yield takeLatest(SET_ACTIVE_USER_PROPERTY, settingActiveProperty);
}

export default function* rootSaga() {
    yield all([
        fork(getProperty),
        fork(getOrgProperties),
        fork(watchSettingActiveProperty),
    ]);
}
